import { useEffect, useMemo, useState } from 'react';

import {
  sendGlobalLayoutProps,
  ENVIRONMENTS,
  waitForElement,
  sendGlobalErrorProps,
} from 'cu-master-layout-prop-manager';
import { hasAuthParams, useAuth as useOidcAuth } from 'react-oidc-context';

const env = ENVIRONMENTS[import.meta.env.VITE_ENVIRONMENT_ID];

type OidcInnerError = {
  error?: string;
  error_description?: string | null;
  error_uri?: string | null;
  session_state?: string;
};

export function useAuth() {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useOidcAuth();
  const token = auth.user?.access_token;
  const fullName = auth.user?.profile?.name;
  const globalLayoutProps = useMemo(
    () => ({
      token: token ?? '',
      env,
      fullName,
      activeLink: window.location.pathname === '/' ? '/cdfi-certification-page' : window.location.pathname,
    }),
    [token, fullName],
  );

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      void auth.signinRedirect();
      setHasTriedSignin(true);
      sendGlobalLayoutProps(globalLayoutProps);
    }
  }, [auth, globalLayoutProps, hasTriedSignin]);

  useEffect(() => {
    if (!hasAuthParams() && auth.isAuthenticated && auth.user?.expired) {
      auth.signinSilent().catch(() => {
        void auth.signinRedirect();
      });
    }
  }, [auth, auth.user?.expired]);

  if (auth.isLoading) {
    return auth;
  }

  if (auth.error) {
    console.error('Auth error. Please check the defined auth credentials', auth.error);
    sendGlobalErrorProps({ errorType: 'generalError' });

    if (
      auth.error.message === 'login_required' ||
      (auth.error.innerError && (auth.error.innerError as OidcInnerError).error === 'login_required')
    ) {
      void auth.signinRedirect();
      return auth;
    }
  }

  if (!auth.isAuthenticated) {
    return auth;
  }

  if (auth.isAuthenticated && token) {
    waitForElement('#master_layout_logout_btn')
      .then((element) => {
        element.removeAttribute('href');
        element.addEventListener('click', () => {
          void auth.signoutRedirect({
            id_token_hint: auth.user?.id_token,
          });
        });
      })
      .catch(console.error);

    sendGlobalLayoutProps(globalLayoutProps);
  }

  return auth;
}
